import { Loader2 } from "lucide-react";

import type { WsState } from "@/lib/wsManager";

interface PodStatusBannerProps {
  isProvisioning: boolean;
  podState: string | null;
  podStateDetail?: string | null;
  wsState: WsState;
}

export function PodStatusBanner({ isProvisioning, podState, podStateDetail, wsState }: PodStatusBannerProps) {
  // Pod intentionally paused — don't show reconnect noise. A detail means the
  // pause was a compute refusal (welcome grant collided/deferred, out of
  // credits) — surface the actionable WHY instead of a silent chat.
  if (podState === "scaled_down") {
    if (podStateDetail) {
      return (
        <div className="border-b border-accent-yellow/30 bg-accent-yellow/10 px-4 py-2.5 text-sm text-accent-yellow">
          <div className="font-bold">Your agent is paused.</div>
          <div className="mt-0.5 text-xs font-normal opacity-80">{podStateDetail}</div>
        </div>
      );
    }
    return null;
  }

  // Pod is being created or starting — show provisioning state
  if (isProvisioning) {
    const label = podState === "starting" ? "Starting your agent..." : "Setting up your agent...";
    return (
      <div className="border-b border-accent-yellow/30 bg-accent-yellow/10 px-4 py-2.5 text-sm text-accent-yellow">
        <div className="flex items-center gap-2 font-bold">
          <Loader2 className="h-4 w-4 animate-spin" />
          {label}
        </div>
        {podStateDetail && (
          <div className="mt-0.5 pl-6 text-xs font-normal opacity-80">{podStateDetail}</div>
        )}
      </div>
    );
  }

  // Pod is running but WS hasn't connected yet
  if (wsState === "error") {
    return (
      <div className="border-b border-brand-coral/30 bg-brand-coral/10 px-4 py-2.5 text-sm font-bold text-brand-coral">
        Connection lost. Retrying...
      </div>
    );
  }

  if (wsState === "connecting") {
    return (
      <div className="flex items-center gap-2 border-b border-accent-yellow/30 bg-accent-yellow/10 px-4 py-2.5 text-sm font-bold text-accent-yellow">
        <Loader2 className="h-4 w-4 animate-spin" />
        Connecting to your agent...
      </div>
    );
  }

  if (wsState === "disconnected") {
    return (
      <div className="flex items-center gap-2 border-b border-accent-yellow/30 bg-accent-yellow/10 px-4 py-2.5 text-sm font-bold text-accent-yellow">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reconnecting...
      </div>
    );
  }

  return null;
}
